// SPDX-License-Identifier: AGPL-3.0-or-later
import {Logger} from '@app/features/platform/utils/AppLogger';
import {ScreenShareSubscriptionManager} from '@app/features/voice/engine/ScreenShareSubscriptionManager';
import {VideoSubscriptionManager} from '@app/features/voice/engine/VideoSubscriptionManager';
import type {Room} from 'livekit-client';
import {makeAutoObservable, runInAction} from 'mobx';

const logger = new Logger('VoiceSubscriptionManager');
export type VideoQualityLevel = 'low' | 'medium' | 'high';
class VoiceSubscriptionManager {
	private room: Room | null = null;
	private videoManager: VideoSubscriptionManager;
	private screenShareManager: ScreenShareSubscriptionManager;
	constructor() {
		makeAutoObservable(this, {}, {autoBind: true});
		this.videoManager = new VideoSubscriptionManager();
		this.screenShareManager = new ScreenShareSubscriptionManager();
		logger.debug('Initialized');
	}
	setRoom(room: Room | null): void {
		if (this.room === room) return;
		if (this.room) {
			this.cleanup();
		}
		runInAction(() => {
			this.room = room;
		});
		this.videoManager.setRoom(room);
		this.screenShareManager.setRoom(room);
		if (room) {
			logger.info('Room set', {participantCount: room.remoteParticipants.size});
		} else {
			logger.info('Room cleared');
		}
	}
	cleanup(): void {
		logger.debug('Cleaning up all subscriptions');
		this.videoManager.cleanup();
		this.screenShareManager.cleanup();
		logger.info('All subscriptions cleaned up');
	}
	subscribeToVideo(
		participantIdentity: string,
		element: HTMLElement | null,
		initialQuality: VideoQualityLevel = 'low',
	): void {
		this.videoManager.subscribe(participantIdentity, element, initialQuality);
	}
	unsubscribeFromVideo(participantIdentity: string): void {
		this.videoManager.unsubscribe(participantIdentity);
	}
	setVideoEnabled(participantIdentity: string, enabled: boolean): void {
		this.videoManager.setEnabled(participantIdentity, enabled);
	}
	setVideoQuality(participantIdentity: string, quality: VideoQualityLevel): void {
		this.videoManager.setQuality(participantIdentity, quality);
	}
	subscribeToScreenShare(
		participantIdentity: string,
		element: HTMLElement | null,
		context: 'focused' | 'carousel' | 'hidden' = 'carousel',
	): void {
		this.screenShareManager.subscribe(participantIdentity, element, context);
	}
	unsubscribeFromScreenShare(participantIdentity: string): void {
		this.screenShareManager.unsubscribe(participantIdentity);
	}
	setScreenShareContext(participantIdentity: string, context: 'focused' | 'carousel' | 'hidden'): void {
		this.screenShareManager.setContext(participantIdentity, context);
	}
	isVideoSubscribed(participantIdentity: string): boolean {
		return this.videoManager.isSubscribed(participantIdentity);
	}
	isScreenShareSubscribed(participantIdentity: string): boolean {
		return this.screenShareManager.isSubscribed(participantIdentity);
	}
	getVideoQuality(participantIdentity: string): VideoQualityLevel | null {
		return this.videoManager.getQuality(participantIdentity);
	}
	getScreenShareContext(participantIdentity: string): 'focused' | 'carousel' | 'hidden' | null {
		return this.screenShareManager.getContext(participantIdentity);
	}
}
const instance = new VoiceSubscriptionManager();
(
	window as typeof window & {
		_voiceSubscriptionManager?: VoiceSubscriptionManager;
	}
)._voiceSubscriptionManager = instance;
export default instance;
